import { useEffect, useRef } from "react";
import styled from "styled-components";
import EmojiPicker from "emoji-picker-react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import {
  AWS_USER_UPLOAD_URL,
  AWS_PROFILE_IMAGES_URL,
  BASE_CPI,
} from "constants";
import Layout from "components/Layout";
import PageTitle from "components/text/PageTitle";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import SidePostPreview from "./SidePostPreview";
import TargetingForm from "./TargetingForm";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import SmallSpinner from "components/other/SmallSpinner";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ButtonWhite from "components/buttons/ButtonWhite";
import TutorialBox from "components/other/TutorialBox";
import Dropdown from "components/forms/Dropdown";
import DropdownSelection from "components/forms/DropdownSelection";
import SelectionItem from "components/forms/SelectionItem";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import ToggleSwitch from "components/forms/ToggleSwitch";
import VideoSelectionContainer from "./VideoSelectionContainer";

import smileyIcon from "assets/img/icons/icons8-smiley.svg";
import aiEditIcon from "assets/img/icons/ai-edit-icon.svg";
import tipIcon from "assets/img/icons/icons8-info.svg";
import coinsIcon from "assets/img/icons/coins-icon.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import returnIcon from "assets/img/icons/return-icon.svg";
import videoIcon from "assets/img/icons/play-video-icon.svg";
import videoWhiteIcon from "assets/img/icons/play-video-icon-white.svg";
import bronzeCheckmarkIcon from "assets/img/icons/checkmark-bronze.svg";
import silverCheckmarkIcon from "assets/img/icons/checkmark-silver.svg";
import goldCheckmarkIcon from "assets/img/icons/checkmark-gold.svg";
import { POST_MAX_LENGTH } from "constants";
import { AD_PLAN_EXTENDED_POST } from "constants";
import { AD_PLAN_EXTENDED_POST_MAX_LENGTH } from "constants";

const CreatePost = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  margin: 0 0 50px;
  padding: 0 28px;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin: 0;
    padding: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const FormBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormHeader = styled.div`
  display: flex;
  gap: ${(props) => (props.mobile ? "8px" : "10px")};
`;

const AvatarIconWrap = styled.div`
  width: ${(props) => (props.mobile ? "48px" : "56px")};
  min-width: ${(props) => (props.mobile ? "48px" : "56px")};
  aspect-ratio: 1;
`;

const AvatarIcon = styled.img`
  width: 100%;
  border-radius: 50%;
`;

const FormHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const UsernameBox = styled.div`
  padding-top: 3px;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const UserName = styled.div`
  padding-top: ${(props) => (props.mobile ? "3px" : "4px")};

  font-size: ${(props) => (props.mobile ? "16px" : "18px")};
  font-weight: 500;
`;

const CheckmarkIcon = styled.div`
  padding-top: ${(props) => (props.mobile ? "4px" : "6px")};

  width: ${(props) => (props.mobile ? "18px" : "20px")};
  min-width: ${(props) => (props.mobile ? "18px" : "20px")};
  height: ${(props) => (props.mobile ? "18px" : "20px")};
`;

const PostInfo = styled.div`
  padding-top: 2px;

  font-size: ${(props) => (props.mobile ? "13px" : "14px")};
  color: ${css.TEXT_GREY};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  flex: 1;
`;

const TextInputBox = styled.div`
  width: 100%;
  padding: 0;
  box-sizing: border-box;

  background: ${css.DARK_BLUE};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 25px;

  font-family: "Roboto";
  font-weight: 300;
  font-size: 15px;
  color: #fff;
  line-height: 125%;

  background: ${css.DARK_BLUE};
  border: 0px;
  border-radius: ${css.BOX_RADIUS_SECONDARY};
  resize: none;
`;

const TextInputFooter = styled.div`
  height: 50px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CharacterCounter = styled.div`
  position: relative;
  margin-left: 25px;
`;

const EmptyCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%);

  border-radius: 50%;
  background: rgb(255, 255, 255, 0.07);
`;

const OuterCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%);

  border-radius: 50%;
  background: ${(props) => (props.atmax ? css.ERROR_RED : css.BRAND_INDIGO)};
  mask: conic-gradient(
    from 0deg,
    #0000,
    #000 1deg ${(props) => props.angle}deg,
    #0000 calc(${(props) => props.angle}deg + 1deg)
  );
`;

const InnerCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  transform: translate(-50%, -50%);

  background: ${css.DARK_BOX_BACKGROUND};
  border-radius: 50%;
`;

const RightControls = styled.div`
  padding: 0 18px 0 25px;

  display: flex;
  align-items: center;
`;

const TextAreaIconCircle = styled.div`
  width: 36px;
  min-width: 36px;
  height: 36px;

  border-radius: 18px;

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: rgb(255, 255, 255, 0.07);
    cursor: pointer;
  }
`;

const TextAreaIcon = styled.div`
  width: ${(props) => props.width};
  min-width: ${(props) => props.width};
  height: ${(props) => props.width};

  &:hover {
    cursor: pointer;
  }
`;

const FormBottom = styled.div`
  position: relative;
  width: 100%;
`;

const PickerWrap = styled.div`
  position: absolute;
  top: ${css.VERTICAL_BOX_MARGIN};
  right: 0;
  z-index: 1;
`;

const FileInput = styled.input`
  width: 100%;
  height: 42px;
  box-sizing: border-box;

  border: 0px;
  border-radius: 15px;

  background: ${css.DARK_BOX_BACKGROUND};

  font-family: "Roboto", sans-serif;
  font-size: 15px;
  font-weight: 300;
  color: #ffffff;

  &::file-selector-button {
    margin: 7px 14px 0 10px;
    height: 28px;
    padding: 0 15px;

    border: 0;
    border-radius: 14px;
    cursor: pointer;
    background: ${css.BRAND_YELLOW};

    transition: 0.2s;

    font-family: "Barlow";
    font-size: 13px;
    font-weight: 500;
  }

  &::file-selector-button:hover {
    color: #fff;

    background: ${css.BRAND_GREEN};
  }
`;

const ImagePreviewBox = styled.div`
  position: relative;
`;

const ImagePreview = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};
  margin-bottom: 6px;

  display: flex;
`;

const ImagePreviewImage = styled.img`
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const DeleteImageOverlay = styled.div`
  position: absolute;
  bottom: 16px;
  right: 10px;
`;

const DeleteImageIcon = styled.div`
  width: 16px;
  height: 16px;

  transition: 0.2s;

  background-image: url(${(props) => props.icon});
  background-repeat: no-repeat;
  background-size: cover;
`;

const DeleteImageCircle = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;

  border-radius: 16px;
  background: ${css.BRAND_YELLOW};

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;

    background: ${css.ERROR_RED};
  }

  &:hover ${DeleteImageIcon} {
    background-image: url(${(props) => props.iconhover});
  }
`;

const Warning = styled.div`
  margin-top: 7px;

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const TargetingInputSection = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  gap: 10px;
`;

const TargetingButtonWrap = styled.div``;

const FormModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 520px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const CpiBox = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};
  padding: ${css.BOX_PADDING_SECONDARY};

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
`;

const CpiBoxTitle = styled.div`
  display: flex;
  align-items: center;
`;

const CpiBoxTitleText = styled.div`
  font-weight: 600;
`;

const TipIcon = styled.div`
  width: 18px;
  min-width: 18px;
  height: 18px;

  margin-left: 6px;

  &:hover {
    cursor: help;
  }
`;

const CpiBoxCpi = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  align-items: center;
`;

const CpiBoxCpiIcon = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const CpiBoxCpiText = styled.div`
  padding-left: 8px;

  font-weight: 600;
`;

const CpiBoxNote = styled.div`
  margin-top: 6px;

  font-size: 13px;
  color: ${css.TEXT_GREY};
`;

const CpiBoxButton = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};
  min-height: 42px;

  display: flex;
  align-items: center;
`;

const CpiBoxForm = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  align-items: center;
`;

const CpiBoxFormInput = styled.div`
  max-width: 80px;
`;

const CpiBoxFormButton = styled.div`
  padding-left: 8px;
`;

const CpiBoxCpiSet = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
  min-height: 42px;

  display: flex;
  align-items: center;
`;

const CpiBoxCpiSetText = styled.div`
  padding-right: 8px;

  line-height: 1;
`;

const PostButton = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};
  margin-bottom: 6px;
`;

const PromptModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: ${(props) => (props.zIndex ? props.zIndex : "200")};

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ConfirmTemplateWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmTemplateText = styled.div``;

const ConfirmTemplateButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 150px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const ViewIntervalSelectionItem = styled.div``;

const Rejection = styled.div`
  margin: 5px 0;

  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
`;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: "#fff";
  white-space: nowrap;

  border-radius: 10px;
  background: ${css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const RejectionReason = styled.div`
  font-size: 15px;
`;

const RunToggleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RunToggleInfo = styled.div`
  font-size: 15px;
`;

const ImageInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const VideoPreviewBox = styled.div`
  width: 100%;

  position: relative;
`;

const VideoPreview = styled.div`
  width: 100%;
  margin-bottom: 6px;

  display: flex;
`;

export default ({
  form,
  text,
  onTextInputChange,
  onSmileyClick,
  pickerOpened,
  onEmojiClick,
  pickerRef,
  usePickerOutsideClick,
  onImageInputChange,
  image,
  onImageLinkInputChange,
  imageLink,
  warnings,
  openTargeting,
  closeTargeting,
  targetingOpened,
  allCountries,
  loading,
  countries,
  updateCountries,
  cpiEstimate,
  openCpiForm,
  closeCpiForm,
  cpiFormOpened,
  onCpiInputChange,
  cpi,
  removeImage,
  imageWarning,
  onPostSubmit,
  submitLoading,
  submitError,
  userProfileData,
  name,
  onNameInputChange,
  getPostTemplate,
  confirmTemplate,
  closeConfirmModal,
  addTemplateText,
  blockModal,
  closeBlockModal,
  confirmLeaveForm,
  tutorialBox,
  closeBox,
  toggleViewIntervalSelection,
  viewIntervalSelectionOpened,
  viewInterval,
  onViewIntervalSelect,
  rejectionReason,
  run,
  onRunToggleClick,
  limit,
  onLimitInputChange,
  placeholdersInText,
  fromPage,
  openVideoSelection,
  upgradePlanModal,
  closeUpgradePlanModal,
  videoSelectionModal,
  closeVideoSelection,
  onVideoSelect,
  video,
  removeVideo,
}) => {
  const textArea = useRef();
  const navigate = useNavigate();
  const viewIntervalDropdownRef = useRef();
  const viewIntervalSelectionRef = useRef();

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  // set page Title
  useEffect(() => {
    document.title =
      form === "create" ? "Create Post | Scalelup" : "Edit Post | Scalelup";
  }, []);

  // handle Textarea height
  useEffect(() => {
    if (!loading) {
      textArea.current.style.height = "105px";

      const requestedHeight = Math.max(textArea.current.scrollHeight, 150);

      textArea.current.style.height = `${Math.min(requestedHeight, 600)}px`;
    }
  }, [text, loading]);

  let textMaximum = POST_MAX_LENGTH;

  if (userProfileData?.ad_plan >= AD_PLAN_EXTENDED_POST) {
    textMaximum = AD_PLAN_EXTENDED_POST_MAX_LENGTH;
  }

  const counterAngle = (360 / textMaximum) * text.length;

  usePickerOutsideClick(pickerRef);

  // prepare Image preview URL address
  const previewLink = AWS_USER_UPLOAD_URL + image;

  // prepare Country Targeting input field content
  let countriesNamesList = "";

  const displayCountries = mobileScreen ? 2 : 4;

  for (let i = 0; i < displayCountries; i++) {
    if (countries[i]) countriesNamesList += countries[i] + ", ";
  }

  let countryInputText = countriesNamesList.slice(0, -2);

  if (countries.length > displayCountries) {
    const remaining = countries.length - displayCountries;

    countryInputText += " (+" + remaining + ")";
  }

  if (allCountries.length === countries.length)
    countryInputText = "All countries";

  if (countries.length === 0) countryInputText = "Select at least one country";

  // prepare Title and Button labels
  const titleText = form === "create" ? "Create New Post" : "Edit Post";
  const buttonText = form === "create" ? "Post" : "Submit";

  // prepare Profile picture
  const profilePictureURL =
    AWS_PROFILE_IMAGES_URL + userProfileData?.profile_image;

  // prepare Placeholders warning text
  let placeholderWarning = "Replace the placeholders with your own text: ";

  for (const placeholder of placeholdersInText) {
    placeholderWarning += placeholder + ", ";
  }

  placeholderWarning = placeholderWarning.substring(
    0,
    placeholderWarning.length - 2
  );

  // prepare back destination
  let navigateBackTo = "/posts";

  if (fromPage) navigateBackTo += `?page=${fromPage}`;

  // prepare Profile header
  let planIcon = goldCheckmarkIcon;
  let checkmarkInfo = "Gold Ad Plan";

  switch (userProfileData?.ad_plan) {
    case 1:
      planIcon = bronzeCheckmarkIcon;
      checkmarkInfo = "Bronze Ad Plan";
      break;
    case 2:
      planIcon = silverCheckmarkIcon;
      checkmarkInfo = "Silver Ad Plan";
      break;
  }

  return (
    <Layout
      sideContent={
        <SidePostPreview
          avatar={profilePictureURL}
          username={userProfileData?.username}
          text={text}
          image={image}
          imageLink={imageLink}
          video={video}
          highlighted={userProfileData?.highlighted_posts}
          showCheckmark={userProfileData?.show_checkmark}
          adPlan={userProfileData?.ad_plan}
        />
      }
    >
      <CreatePost>
        {tutorialBox && (
          <TutorialBox
            title="Create Your First Post"
            text="Start by creating a post. Get inspired by clicking on the magic pencil icon. You can also include a link to your website. Users can visit your site or contact you via private message."
            closeBox={closeBox}
          />
        )}
        <Header>
          <PageTitle>{titleText}</PageTitle>
          <BackIconWrap>
            <RoundActionIcon
              icon={returnIcon}
              iconSize="16px"
              iconAlt="Return"
              onClick={() => navigate(navigateBackTo)}
            />
          </BackIconWrap>
        </Header>
        <FormBox>
          {loading && <BoxLoadingSpinner />}
          {!loading && (
            <>
              <FormHeader mobile={mobileScreen ? 1 : 0}>
                <AvatarIconWrap mobile={mobileScreen ? 1 : 0}>
                  <AvatarIcon
                    src={profilePictureURL}
                    style={{ width: "100%" }}
                  />
                </AvatarIconWrap>
                <FormHeaderContent>
                  <UsernameBox>
                    <UserName mobile={mobileScreen ? 1 : 0}>
                      {userProfileData?.username}
                    </UserName>
                    {userProfileData?.show_checkmark &&
                      userProfileData?.ad_plan > 0 && (
                        <CheckmarkIcon
                          mobile={mobileScreen ? 1 : 0}
                          data-tooltip-id="checkmark-info"
                          data-tooltip-html={checkmarkInfo}
                          data-tooltip-place="top"
                          data-tooltip-delay-show={250}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img src={planIcon} style={{ width: "100%" }} />
                        </CheckmarkIcon>
                      )}
                    <Tooltip id="checkmark-info" className="tooltip" />
                  </UsernameBox>
                  <PostInfo mobile={mobileScreen ? 1 : 0}>
                    Post to Anyone
                  </PostInfo>
                </FormHeaderContent>
              </FormHeader>
              {rejectionReason && (
                <Rejection>
                  <StatusBadge>Rejected</StatusBadge>
                  <RejectionReason>
                    The post was not approved because it violates the rules.
                    Reason for rejection: {rejectionReason}. Please correct the
                    post and submit it again.
                  </RejectionReason>
                </Rejection>
              )}
              <Content>
                <TextInputBox>
                  <TextArea
                    ref={textArea}
                    name="text"
                    value={text}
                    placeholder="Share your business offer or anything you want..."
                    maxLength={textMaximum}
                    onChange={onTextInputChange}
                  />
                  <TextInputFooter>
                    <CharacterCounter>
                      <EmptyCircle />
                      <OuterCircle
                        angle={counterAngle}
                        atmax={text.length === textMaximum ? 1 : 0}
                      />
                      <InnerCircle />
                    </CharacterCounter>
                    <RightControls>
                      <TextAreaIconCircle onClick={onSmileyClick}>
                        <TextAreaIcon width="20px">
                          <img src={smileyIcon} style={{ width: "100%" }} />
                        </TextAreaIcon>
                      </TextAreaIconCircle>
                      <TextAreaIconCircle
                        onClick={getPostTemplate}
                        data-tooltip-id="templateTip"
                        data-tooltip-content="Click to insert a sample template text that will help you start creating an engaging post."
                        data-tooltip-place="top"
                        data-tooltip-delay-show={500}
                      >
                        <TextAreaIcon width="18px">
                          <img src={aiEditIcon} style={{ width: "100%" }} />
                        </TextAreaIcon>
                      </TextAreaIconCircle>
                      <Tooltip id="templateTip" className="tooltip" />
                    </RightControls>
                  </TextInputFooter>
                </TextInputBox>
                <FormBottom>
                  <PickerWrap ref={pickerRef}>
                    <EmojiPicker
                      open={pickerOpened}
                      onEmojiClick={onEmojiClick}
                      autoFocusSearch={false}
                      theme="dark"
                      width={280}
                      height={310}
                    />
                  </PickerWrap>
                </FormBottom>
                {warnings.includes("text") && (
                  <Warning>
                    The post must be at least 20 characters long.
                  </Warning>
                )}
                {warnings.includes("placeholdersInText") && (
                  <Warning>{placeholderWarning}</Warning>
                )}
                {!video && (
                  <InputWithLabel
                    label="Add Image (optional)"
                    width="100%"
                    marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  >
                    <ImageInputWrap>
                      <FileInput
                        type="file"
                        accept="image/png, image/gif, image/jpeg"
                        onChange={onImageInputChange}
                      />
                      {userProfileData.ad_plan >= 2 && (
                        <ButtonRoundIcon
                          icon={videoIcon}
                          hoverIcon={videoWhiteIcon}
                          background={css.BRAND_YELLOW}
                          hoverBackground={css.BRAND_GREEN}
                          iconSize="14px"
                          onClick={openVideoSelection}
                        />
                      )}
                    </ImageInputWrap>
                  </InputWithLabel>
                )}
                {imageWarning && (
                  <Warning>
                    The selected file is too large. The maximum allowed size is
                    6000 kB.
                  </Warning>
                )}
                {image && (
                  <ImagePreviewBox>
                    <ImagePreview>
                      <ImagePreviewImage src={previewLink} />
                    </ImagePreview>
                    <DeleteImageOverlay>
                      <DeleteImageCircle
                        iconhover={binWhiteIcon}
                        onClick={removeImage}
                      >
                        <DeleteImageIcon icon={binIcon} />
                      </DeleteImageCircle>
                    </DeleteImageOverlay>
                  </ImagePreviewBox>
                )}
                {video && (
                  <InputWithLabel
                    label="Selected Video"
                    width="100%"
                    marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  >
                    <VideoPreviewBox>
                      <VideoPreview>
                        <div
                          style={{
                            position: "relative",
                            paddingTop: "56.25%",
                            width: "100%",
                          }}
                        >
                          <iframe
                            src={`https://iframe.mediadelivery.net/embed/394352/${video}?autoplay=false&loop=true&muted=true&preload=true&responsive=true`}
                            loading="lazy"
                            style={{
                              border: 0,
                              position: "absolute",
                              top: 0,
                              left: 0,
                              height: "100%",
                              width: "100%",
                            }}
                            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                            allowfullscreen="false"
                          ></iframe>
                        </div>
                      </VideoPreview>
                      <DeleteImageOverlay>
                        <DeleteImageCircle
                          iconhover={binWhiteIcon}
                          onClick={removeVideo}
                        >
                          <DeleteImageIcon icon={binIcon} />
                        </DeleteImageCircle>
                      </DeleteImageOverlay>
                    </VideoPreviewBox>
                  </InputWithLabel>
                )}
                {!video && (
                  <InputWithLabel
                    label="Image Destination Link (optional)"
                    width="100%"
                    marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                    tip="Enter the URL to which the user will be directed when they click on your image."
                    tipId="imageLink"
                  >
                    <InputText
                      type="text"
                      name="link"
                      value={imageLink}
                      placeholder="https://..."
                      onChange={onImageLinkInputChange}
                      maxLength={400}
                      warning={warnings.includes("link")}
                    />
                  </InputWithLabel>
                )}
                {warnings.includes("imageLink") && (
                  <Warning>
                    The entered URL is invalid. The address should start with
                    https://
                  </Warning>
                )}
                {warnings.includes("imageLinkNoImage") && (
                  <Warning>
                    You haven't uploaded any image. The image link cannot be
                    inserted.
                  </Warning>
                )}
                <InputWithLabel
                  label="Country Targeting"
                  width="100%"
                  marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  tip="Here you can choose the countries where your post will be displayed."
                  tipId="targeting"
                >
                  <TargetingInputSection>
                    <InputText
                      type="text"
                      name="link"
                      value={countryInputText}
                      warning={warnings.includes("link")}
                      readOnly={true}
                    />
                    <TargetingButtonWrap>
                      <ButtonPrimarySmall onClick={openTargeting}>
                        Change
                      </ButtonPrimarySmall>
                    </TargetingButtonWrap>
                  </TargetingInputSection>
                </InputWithLabel>
                {warnings.includes("targeting") && (
                  <Warning>Select at least one target country.</Warning>
                )}
                {/* <InputWithLabel
                  label="View Interval"
                  width="100%"
                  marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  tip="<b>Coming soon</b> - this feature is not yet available. The display interval is set to 1 day by default.<br><br>Select how often the post can be shown to the same user. A shorter interval means the user will see your post more frequently, and vice versa."
                    tipId="viewInterval"
                >
                  <DropdownWrap>
                    <Dropdown
                      onClick={toggleViewIntervalSelection}
                      dropdownRef={viewIntervalDropdownRef}
                      disabled
                    >
                      <ViewIntervalSelectionItem>
                        {viewInterval} day{viewInterval > 1 ? "s" : ""}
                      </ViewIntervalSelectionItem>
                    </Dropdown>
                    {viewIntervalSelectionOpened && (
                      <DropdownSelection
                        width="150px"
                        top="48px"
                        selectionRef={viewIntervalSelectionRef}
                      >
                        <SelectionItem onClick={() => onViewIntervalSelect(1)}>
                          <ViewIntervalSelectionItem>
                            1 day
                          </ViewIntervalSelectionItem>
                        </SelectionItem>
                        <SelectionItem onClick={() => onViewIntervalSelect(3)}>
                          <ViewIntervalSelectionItem>
                            3 days
                          </ViewIntervalSelectionItem>
                        </SelectionItem>
                        <SelectionItem onClick={() => onViewIntervalSelect(9)}>
                          <ViewIntervalSelectionItem>
                            9 days
                          </ViewIntervalSelectionItem>
                        </SelectionItem>
                        <SelectionItem onClick={() => onViewIntervalSelect(27)}>
                          <ViewIntervalSelectionItem>
                            27 days
                          </ViewIntervalSelectionItem>
                        </SelectionItem>
                      </DropdownSelection>
                    )}
                  </DropdownWrap>
                </InputWithLabel> */}
                <CpiBox>
                  <CpiBoxTitle>
                    <CpiBoxTitleText>CPI (Cost per Impression)</CpiBoxTitleText>
                    <TipIcon
                      data-tooltip-id="cpiTip"
                      data-tooltip-html="The cost per impression determines how many coins will be deducted from your balance when a user views your post. The price primarily depends on the user's location. The displayed price range corresponds to the chosen targeting of the post."
                      data-tooltip-place="top"
                      data-tooltip-delay-show={500}
                    >
                      <img
                        src={tipIcon}
                        style={{ width: "100%", paddingTop: "1px" }}
                      />
                    </TipIcon>
                    <Tooltip id="cpiTip" className="tooltip" />
                  </CpiBoxTitle>
                  <CpiBoxCpi>
                    <CpiBoxCpiIcon>
                      <img
                        src={coinsIcon}
                        style={{ width: "100%", paddingTop: "1px" }}
                      />
                    </CpiBoxCpiIcon>
                    <CpiBoxCpiText>
                      {cpiEstimate.min === cpiEstimate.max
                        ? cpiEstimate.min
                        : `${cpiEstimate.min} - ${cpiEstimate.max}`}{" "}
                      Coins
                    </CpiBoxCpiText>
                  </CpiBoxCpi>
                  <CpiBoxNote>* CPI can change over time</CpiBoxNote>
                  {/* {!cpiFormOpened && cpi === "" && (
                    <CpiBoxButton>
                      <ButtonPrimarySmall onClick={openCpiForm}>
                        Set Maximum CPI
                      </ButtonPrimarySmall>
                    </CpiBoxButton>
                  )}
                  {cpiFormOpened && (
                    <CpiBoxForm>
                      <CpiBoxFormInput>
                        <InputText
                          type="text"
                          name="cpi"
                          value={cpi}
                          onChange={onCpiInputChange}
                          maxLength="4"
                        />
                      </CpiBoxFormInput>
                      <CpiBoxFormButton>
                        <ButtonPrimarySmall onClick={closeCpiForm}>
                          OK
                        </ButtonPrimarySmall>
                      </CpiBoxFormButton>
                    </CpiBoxForm>
                  )} */}
                  {/* {!cpiFormOpened && cpi !== "" && (
                    <CpiBoxCpiSet>
                      <CpiBoxCpiSetText>Max. CPI: {cpi}</CpiBoxCpiSetText>
                      <ButtonPrimarySmall onClick={openCpiForm}>
                        Change
                      </ButtonPrimarySmall>
                    </CpiBoxCpiSet>
                  )} */}
                </CpiBox>
                {warnings.includes("cpi") && (
                  <Warning>
                    The minimum CPI value is {BASE_CPI}. Please enter a higher
                    value.
                  </Warning>
                )}
                <InputWithLabel
                  label="Daily Spend Limit (optional)"
                  width="100%"
                  marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  tip="You can specify how many coins this post can spend each day. The actual expenditure may be slightly higher.<br><br>Leave blank for no limit."
                  tipId="spendLimit"
                >
                  <InputText
                    type="text"
                    name="limit"
                    width="150px"
                    value={limit}
                    placeholder="No limit set"
                    onChange={onLimitInputChange}
                    maxLength={6}
                  />
                </InputWithLabel>
                {warnings.includes("limit") && (
                  <Warning>
                    The limit can be set to a minimum of 100 coins.
                  </Warning>
                )}
                <InputWithLabel
                  label="Post Name (optional)"
                  width="100%"
                  marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                  tip="The post name will not be visible to anyone else."
                  tipId="postName"
                >
                  <InputText
                    type="text"
                    name="name"
                    value={name}
                    placeholder="Enter the post name for your reference."
                    onChange={onNameInputChange}
                    maxLength={30}
                  />
                </InputWithLabel>
                {form === "create" && (
                  <InputWithLabel
                    label="Run Post After Validation"
                    width="100%"
                    marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
                    tip="Choose whether the post should automatically start displaying once approved or remain paused so you can launch it manually later."
                    tipId="run"
                  >
                    <RunToggleLine>
                      <ToggleSwitch
                        on={run}
                        onClick={() => onRunToggleClick()}
                      />
                      {run && (
                        <RunToggleInfo>
                          The post will be automatically launched once approved.
                        </RunToggleInfo>
                      )}
                      {!run && (
                        <RunToggleInfo>
                          The post will be paused once approved.
                        </RunToggleInfo>
                      )}
                    </RunToggleLine>
                  </InputWithLabel>
                )}
                <PostButton>
                  <ButtonPrimary
                    onClick={onPostSubmit}
                    disabled={submitLoading}
                    minWidth="111px"
                  >
                    {submitLoading ? <SmallSpinner /> : buttonText}
                  </ButtonPrimary>
                </PostButton>
                {warnings.length !== 0 && (
                  <Warning>
                    There are errors in the form. Please check the entered
                    information.
                  </Warning>
                )}
                {submitError && <Warning>{submitError}</Warning>}
              </Content>
            </>
          )}
        </FormBox>
      </CreatePost>
      {targetingOpened && (
        <>
          <Backdrop
            onClick={closeTargeting}
            parameters="login"
            background="rgba(0, 0, 0, 0.7)"
          />
          <FormModalWrap>
            <ModalWindow
              handleModalClose={closeTargeting}
              fullScreenHeight={true}
              title="Country Targeting"
            >
              <TargetingForm
                allCountries={allCountries}
                selectedCountries={countries}
                updateCountries={updateCountries}
                closeTargeting={closeTargeting}
                cpi={cpiEstimate}
              />
            </ModalWindow>
          </FormModalWrap>
        </>
      )}
      {confirmTemplate && (
        <>
          <Backdrop
            onClick={closeConfirmModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <PromptModal>
            <ModalWindow
              title="Add Post Template"
              handleModalClose={closeConfirmModal}
              fullScreenHeight={true}
            >
              <ConfirmTemplateWrap>
                <ConfirmTemplateText>
                  Inserting the template text will remove the content you have
                  created so far. Do you want to proceed?
                </ConfirmTemplateText>
                <ConfirmTemplateButtons>
                  <ButtonWhite onClick={closeConfirmModal}>Cancel</ButtonWhite>
                  <ButtonPrimary onClick={addTemplateText}>
                    Continue
                  </ButtonPrimary>
                </ConfirmTemplateButtons>
              </ConfirmTemplateWrap>
            </ModalWindow>
          </PromptModal>
        </>
      )}
      {blockModal && (
        <>
          <Backdrop
            onClick={closeBlockModal}
            background="rgba(0, 0, 0, 0.7)"
            zIndex="1900"
          />
          <PromptModal zIndex="2000">
            <ModalWindow
              title="Leave and Discard Changes"
              handleModalClose={closeBlockModal}
              fullScreenHeight={true}
            >
              <ConfirmTemplateWrap>
                <ConfirmTemplateText>
                  You are about to leave the post editing form. Any changes you
                  have made will be lost.
                </ConfirmTemplateText>
                <ConfirmTemplateButtons>
                  <ButtonWhite onClick={closeBlockModal}>Cancel</ButtonWhite>
                  <ButtonPrimary onClick={confirmLeaveForm}>
                    Leave
                  </ButtonPrimary>
                </ConfirmTemplateButtons>
              </ConfirmTemplateWrap>
            </ModalWindow>
          </PromptModal>
        </>
      )}
      {upgradePlanModal && (
        <>
          <Backdrop
            onClick={closeUpgradePlanModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <PromptModal>
            <ModalWindow
              title="Insert Video"
              handleModalClose={closeUpgradePlanModal}
              fullScreenHeight={true}
            >
              <ConfirmTemplateWrap>
                <ConfirmTemplateText>
                  Inserting videos into posts is only available for owners of
                  the Silver or Gold advertising plan.
                </ConfirmTemplateText>
                <ConfirmTemplateButtons>
                  <ButtonWhite onClick={closeUpgradePlanModal}>
                    Cancel
                  </ButtonWhite>
                  <ButtonPrimary onClick={() => navigate("/ad-plan")}>
                    Upgrade
                  </ButtonPrimary>
                </ConfirmTemplateButtons>
              </ConfirmTemplateWrap>
            </ModalWindow>
          </PromptModal>
        </>
      )}
      {videoSelectionModal && (
        <>
          <Backdrop
            onClick={closeVideoSelection}
            parameters="login"
            background="rgba(0, 0, 0, 0.7)"
          />
          <FormModalWrap>
            <ModalWindow
              handleModalClose={closeVideoSelection}
              fullScreenHeight={true}
              title="Select Video"
            >
              <VideoSelectionContainer
                closeVideoSelection={closeVideoSelection}
                onVideoSelect={onVideoSelect}
              />
            </ModalWindow>
          </FormModalWrap>
        </>
      )}
    </Layout>
  );
};
